import React, { useState } from "react"
import { Input, Tabs, Button, Empty, Row, Col, Card } from "antd"
import { SearchOutlined, PlusOutlined } from "@ant-design/icons"
import { CustomTemplateCard } from "./CustomTemplateCard"
import styles from "./CustomTemplateHeader.module.css"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import TemplateEmptyImage from "ui/Icon/DSIcon/TemplateEmptyImage"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { AddNewTemplate } from "./modal/AddNewTemplate"
import CustomPagination from "components/common/CustomPagination"
import Spinner from "components/common/Spinner"
import { useTemplates } from "./hooks/useTemplates"
import { useTabItems } from "./hooks/useTabItems"

export const CustomTemplate = () => {
  const [tab, setTab] = useState("published")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [searchText, setSearchText] = useState("")
  const { t } = useTranslation()

  // Use custom hook for templates data and pagination
  const { templates, loading, metadata, pagination, handlePageChange, handlePageSizeChange, handleTabChange } = useTemplates(
    tab,
    searchText
  )

  // Use custom hook for tab items
  const tabItems = useTabItems(metadata, styles)

  const handleAddTemplate = () => {
    setEditingTemplate(null)
    setIsModalOpen(true)
  }

  const handleCancelAdd = () => setIsModalOpen(false)

  const handleSaveAdd = (values) => {
    // handle save logic here
    setIsModalOpen(false)
  }

  const handleEditTemplate = (template) => {
    setEditingTemplate(template)
    setIsModalOpen(true)
  }

  const onTabChange = (key) => {
    setTab(key)
    handleTabChange(key)
  }

  return (
    <div style={{ width: "100%" }}>
      <div className={styles.headerBar}>
        <Input
          className={styles.searchInput}
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: "#bdbdbd" }} />}
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
        />
        <Button
          color="primary"
          size="large"
          variant="solid"
          className={styles.addButton}
          icon={<PlusOutlined aria-hidden="true" />}
          onClick={handleAddTemplate}
        >
          Add Template
        </Button>
      </div>

      <Tabs className={styles.tabs} activeKey={tab} items={tabItems} onChange={onTabChange} />

      <WithEmptyStateWrapper
        fallback={
          loading ? (
            <Spinner />
          ) : (
            <Card className={styles.contentWrapper}>
              <Empty
                image={<TemplateEmptyImage />}
                description={
                  <>
                    <Text strong size="lg">
                      {tab === "published" ? t("settings.not_created_any_template") : "You have no draft templates."}
                    </Text>
                    <p style={{ color: "#888" }}>
                      {tab === "published"
                        ? t("settings.your_template_displayed_here")
                        : "Your draft template will be displayed here."}
                    </p>
                    {tab === "published" && (
                      <Button color="primary" variant="solid" onClick={handleAddTemplate}>
                        Add Template
                      </Button>
                    )}
                  </>
                }
              />
            </Card>
          )
        }
        hasItems={templates.length > 0}
        name="customTemplate"
        tag="div"
        className="vstack gap-20"
      >
        <Row gutter={[12, 12]}>
          {templates.map((item) => (
            <Col xs={24} sm={12} lg={12} key={item.id}>
              <CustomTemplateCard
                key={item.id}
                id={item.id}
                title={item.name}
                description={item.description}
                status={item.status}
                timeAgo={item.updated_at}
                publishedTags={item.trips?.map((trip) => ({ label: trip.title, value: trip.id })) || []}
                onEdit={handleEditTemplate}
              />
            </Col>
          ))}
        </Row>
        {templates.length > 0 && (
          <div style={{ marginTop: 24, textAlign: "right" }}>
            <CustomPagination
              metadata={{
                ...metadata,
                page: metadata.page || 1,
                total_pages: metadata.total_pages || 1,
                next_page: metadata.next_page || null,
                prev_page: metadata.prev_page || null
              }}
              pageSize={pagination.pageSize}
              total={metadata.total_results || 0}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </WithEmptyStateWrapper>

      {isModalOpen && (
        <AddNewTemplate 
          visible={isModalOpen} 
          onCancel={handleCancelAdd} 
          onSave={handleSaveAdd}
          template={editingTemplate}
          tab={tab}
        />
      )}
    </div>
  )
}
